import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Backpack, BookOpen, CalendarClock, Circle, CircleAlert, CircleCheck, ExternalLink, FileText, Lightbulb, Plus, RotateCcw, Sigma, TriangleAlert } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { plainText, type DashboardItem, type DashboardQuota, type DashboardSource, type Everything, type SmActionType } from '@tempo/core';
import { api } from '../api';
import { EmailList } from '../components/assistant/EmailTool';
import { RedAlertPanel } from '../components/RedAlert';
import { Button, Loading, Segmented } from '../components/ui';
import { dayName, minutes, relative, time } from '../lib/format';
import { useNow } from '../lib/hooks';
import { toast } from '../lib/toast';

const DAY = 86_400_000;
const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN);
const when = (iso: string | null | undefined) => {
  const t = ms(iso);
  return Number.isNaN(t) ? null : `${dayName(t)} ${time(t)}`;
};
const ago = (iso: string | null | undefined, now: number) => {
  const t = ms(iso);
  return Number.isNaN(t) ? 'never' : relative(t, now);
};
const dueKey = (i: DashboardItem) => {
  const t = ms(i.due);
  return Number.isNaN(t) ? Infinity : t;
};
const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

/** School Manager's check states → badge tone + words. "overdue" = the routine didn't run when it should have. */
const STATE: Record<string, { tone: 'ok' | 'warn' | 'danger' | ''; label: string }> = {
  ok: { tone: 'ok', label: 'Checked' },
  overdue: { tone: 'warn', label: 'Overdue' },
  failed: { tone: 'danger', label: 'Failed' },
  'signed out': { tone: 'danger', label: 'Signed out' },
  missing: { tone: 'warn', label: 'Missing' },
  'not yet': { tone: '', label: 'Not yet' },
  'not set up': { tone: '', label: 'Not set up' },
};
const stateOf = (s: string) => STATE[s] ?? { tone: 'warn' as const, label: s };

function Section({ id, title, aside, children }: { id: string; title: string; aside?: ReactNode; children: ReactNode }) {
  return (
    <section className="ev-section" id={id}>
      <div className="ev-head">
        <h2 className="section-title">{title}</h2>
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Has everything been checked? One tile per source School Manager watches; tap one for what it found. */
function Checked({ sources, now }: { sources: DashboardSource[]; now: number }) {
  const [open, setOpen] = useState<string | null>(null);
  if (!sources.length) return <p className="subtle small">School Manager hasn't reported any checks yet.</p>;
  return (
    <div className="ev-checks">
      {sources.map((s) => {
        const st = stateOf(s.state);
        return (
          <button key={s.key} type="button" className={`ev-check ${st.tone} ${open === s.key ? 'open' : ''}`} onClick={() => setOpen(open === s.key ? null : s.key)}>
            <span className="ev-check-top">
              <b>{s.label}</b>
              <span className={`badge ${st.tone}`}>{st.label}</span>
            </span>
            <span className="tiny faint">{s.checkedAt ? `checked ${ago(s.checkedAt, now)} · ${when(s.checkedAt)}` : 'not checked yet'}</span>
            {s.detail && <span className="ev-detail small subtle">{s.detail}</span>}
          </button>
        );
      })}
    </div>
  );
}

/** The short list of things that need you, each jumping to its section. */
function Attention({ d, now }: { d: Everything; now: number }) {
  const open = d.items.filter((i) => i.status === 'open');
  const overdue = open.filter((i) => i.overdue || dueKey(i) < now);
  const unbooked = open.filter((i) => i.noTimeBooked);
  const urgent = d.mail.needsYou.filter((m) => m.priority === 1).length;
  const lines: { tone: 'danger' | 'warn'; text: string; target: string | null }[] = [];
  const n = d.mail.needsYou.length;
  if (n) lines.push({ tone: urgent ? 'danger' : 'warn', text: `${n} email${n === 1 ? ' needs' : 's need'} you${urgent ? ` (${urgent} urgent)` : ''}`, target: 'ev-mail' });
  if (overdue.length) lines.push({ tone: 'danger', text: `${overdue.length} overdue`, target: 'ev-work' });
  if (unbooked.length) lines.push({ tone: 'warn', text: `${unbooked.length} with work left but no study time booked yet`, target: 'ev-work' });
  for (const q of d.quotas.filter((x) => x.atRisk)) {
    lines.push({ tone: 'danger', text: `${q.module} quizzes at ${q.pct}%${q.requiredPct != null ? ` – needs ${q.requiredPct}% to sit the exam` : ''}`, target: 'ev-quizzes' });
  }
  for (const s of d.sources.filter((x) => x.state !== 'ok' && x.state !== 'not yet' && x.state !== 'not set up')) {
    lines.push({ tone: stateOf(s.state).tone === 'danger' ? 'danger' : 'warn', text: `${s.label}: ${stateOf(s.state).label.toLowerCase()}${s.checkedAt ? ` (last checked ${ago(s.checkedAt, now)})` : ''}`, target: 'ev-checked' });
  }
  for (const p of d.planIssues.filter((x) => x.severity === 'error').slice(0, 3)) lines.push({ tone: 'warn', text: p.message, target: null });

  if (!lines.length) return <p className="subtle">Nothing needs you right now.</p>;
  return (
    <ul className="ev-attention">
      {lines.map((l, i) => (
        <li key={i} className={l.tone}>
          {l.tone === 'danger' ? <CircleAlert size={16} /> : <TriangleAlert size={16} />}
          {l.target ? (
            <button type="button" onClick={() => jump(l.target!)}>
              {l.text}
            </button>
          ) : (
            <span>{l.text}</span>
          )}
        </li>
      ))}
    </ul>
  );
}

// School Manager writes maths as plain text: C_B, A_M, l_0 for subscripts and x^2, e^(−Q/RT) for powers.
const MATH = /([A-Za-zΔ∇ρσενθ])_([A-Za-z0-9]{1,4})|\^\(([^)]{1,40})\)|\^([0-9A-Za-z+\-−]{1,6})/g;

/** Text with subscripts and powers set properly (built from React nodes, so nothing is parsed as HTML). */
function MathText({ text }: { text: string }) {
  const out: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const m of text.matchAll(MATH)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    if (m[1]) {
      out.push(
        <span key={key++}>
          {m[1]}
          <sub>{m[2]}</sub>
        </span>,
      );
    } else {
      out.push(<sup key={key++}>{m[3] ?? m[4]}</sup>);
    }
    last = at + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return <>{out}</>;
}

/** #/everything?item=<id> (from a note's "Work on this"): that item, opened and scrolled to. */
const focusedItem = () => new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('item');

function WorkItem({ it, now, onAct }: { it: DashboardItem; now: number; onAct: (type: SmActionType, it: DashboardItem) => void }) {
  const focused = focusedItem() === it.id;
  const [open, setOpen] = useState(focused);
  const row = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) {
      setOpen(true);
      row.current?.scrollIntoView({ block: 'center' });
    }
  }, [focused]);
  const done = it.status !== 'open';
  const due = ms(it.due);
  const upcoming = it.booked.filter((b) => ms(b.end) > now && b.status !== 'missed');
  const meta = [
    it.module,
    it.kind,
    !Number.isNaN(due) && `due ${dayName(due)} ${time(due)} (${relative(due, now)})`,
    it.minutesLeft != null && it.minutesLeft > 0 && `${minutes(it.minutesLeft)} left`,
  ].filter(Boolean);
  return (
    <li ref={row} className={`ev-item ${done ? 'done' : ''} ${!done && (it.overdue || due < now) ? 'late' : ''}${focused ? ' focused' : ''}`}>
      <button type="button" className="ev-tick" aria-label={done ? 'Reopen' : 'Mark done'} title={done ? 'Reopen' : 'Mark done'} onClick={() => onAct(done ? 'reopen' : 'done', it)} disabled={it.pending === 'add'}>
        {done ? <CircleCheck size={20} /> : <Circle size={20} />}
      </button>
      <div className="ev-item-body">
        <button type="button" className="ev-title" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {it.title}
        </button>
        <div className="tiny faint">{meta.join(' · ')}</div>
        <div className="ev-badges">
          {it.noTimeBooked && <span className="badge warn">No study time booked yet</span>}
          {upcoming.length > 0 && <span className="badge ok">Booked {when(upcoming[0].start)}</span>}
          {it.pending && <span className="badge">{it.pending === 'add' ? 'Added here' : it.pending === 'done' ? 'Ticked here' : 'Reopened here'} · School Manager will catch up</span>}
          {it.bbStatus && <span className="badge">{it.bbStatus}</span>}
        </div>
        {open && (
          <div className="ev-more">
            {it.about && (
              <p className="ev-about">
                <MathText text={plainText(it.about)} />
              </p>
            )}
            {!done && (it.guide || it.related.length > 0) && (
              <div className="ev-study-ask">
                <BookOpen size={16} />
                <span className="grow">Want to go through the material for this?</span>
                <a className="btn sm primary" href={`#/notes?guide=${encodeURIComponent(it.id)}`}>
                  Yes, open it
                </a>
              </div>
            )}
            {it.links.length > 0 && (
              <div className="ev-links">
                {it.links.map((l, i) =>
                  l.available ? (
                    <a key={i} className={`btn sm${l.kind === 'file' ? ' primary' : ''}`} href={l.href} target="_blank" rel="noopener noreferrer">
                      {l.kind === 'file' ? <FileText size={13} /> : <ExternalLink size={13} />} {l.label}
                    </a>
                  ) : (
                    <span key={i} className="btn sm" aria-disabled="true" title="School Manager hasn't saved this file yet">
                      <FileText size={13} /> {l.label} (not saved yet)
                    </span>
                  ),
                )}
              </div>
            )}
            {(it.session || it.bring.length > 0 || (!it.session && it.location)) && (
              <div className="ev-session">
                {(it.session || it.location) && (
                  <div>
                    <CalendarClock size={14} /> {it.session ?? it.location}
                  </div>
                )}
                {it.bring.length > 0 && (
                  <div>
                    <Backpack size={14} /> Bring: {it.bring.join(' · ')}
                  </div>
                )}
              </div>
            )}
            <div className="ev-sec">
              <div className="ev-sec-title">Do this</div>
              {it.steps.length > 0 ? (
                <ol className="ev-steps">
                  {it.steps.map((s, i) => (
                    <li key={i}>
                      <MathText text={plainText(s)} />
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="small subtle">No steps from School Manager yet.</div>
              )}
            </div>
            {it.formulas.length > 0 && (
              <div className="ev-sec">
                <div className="ev-sec-title">
                  <Sigma size={14} /> Formulas
                </div>
                <dl className="ev-formulas">
                  {it.formulas.map((f, i) => (
                    <div key={i} className="ev-formula">
                      {f.name && <dt>{f.name}</dt>}
                      <dd>
                        <MathText text={f.expr} />
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}
            {it.hints.length > 0 && (
              <details className="ev-fold">
                <summary>
                  <Lightbulb size={14} /> Hints ({it.hints.length})
                </summary>
                <ul className="ev-hints">
                  {it.hints.map((h, i) => (
                    <li key={i}>
                      {h.label && <div className="ev-hint-label">{h.label}</div>}
                      <MathText text={plainText(h.text)} />
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {it.answers && (
              <details className="ev-fold">
                <summary>Answers (tap to show)</summary>
                <div className="ev-answers">
                  <MathText text={it.answers} />
                </div>
              </details>
            )}
            {it.notes && <div className="ev-notes small">{plainText(it.notes)}</div>}
            {it.mark && it.mark !== 'Not marked' && <div className="small subtle">Mark: {it.mark}</div>}
            {it.url && !it.links.some((l) => l.href === it.url) && (
              <a className="btn sm" href={it.url} target="_blank" rel="noopener noreferrer" style={{ alignSelf: 'flex-start' }}>
                <ExternalLink size={13} /> Open{it.source === 'blackboard' ? ' in Blackboard' : ''}
              </a>
            )}
            {(it.estMinutes != null || it.booked.length > 0) && (
              <div className="ev-study small subtle">
                {[it.estMinutes != null && `About ${minutes(it.estMinutes)} of work`, it.minutesLeft != null && `${minutes(Math.max(0, it.minutesLeft))} left`].filter(Boolean).join(' · ')}
                {it.booked.length > 0 && (
                  <>
                    {' · Study time: '}
                    {it.booked.map((b, i) => (
                      <span key={i} className={ms(b.end) < now ? 'faint' : ''}>
                        {i > 0 && ', '}
                        {when(b.start)}–{time(ms(b.end))}
                        {b.status === 'done' ? ' ✓' : b.status && b.status !== 'planned' ? ` (${b.status})` : ''}
                      </span>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Group({ title, items, now, open, onAct }: { title: string; items: DashboardItem[]; now: number; open: boolean; onAct: (t: SmActionType, it: DashboardItem) => void }) {
  if (!items.length) return null;
  const focus = focusedItem();
  return (
    <details className="ev-group" open={open || items.some((i) => i.id === focus)}>
      <summary>
        {title} <span className="faint">({items.length})</span>
      </summary>
      <ul className="ev-items">
        {items.map((it) => (
          <WorkItem key={it.id} it={it} now={now} onAct={onAct} />
        ))}
      </ul>
    </details>
  );
}

function QuickTodo({ onAdd }: { onAdd: (title: string, due: string | null) => Promise<unknown> }) {
  const [title, setTitle] = useState('');
  const [due, setDue] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    void onAdd(title.trim(), due || null).then(() => {
      setTitle('');
      setDue('');
    });
  };
  return (
    <form className="ev-add" onSubmit={submit}>
      <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Add a to-do…" aria-label="New to-do" maxLength={300} />
      <input className="input" type="date" value={due} onChange={(e) => setDue(e.target.value)} aria-label="Due date (optional)" />
      <Button type="submit" variant="primary" disabled={!title.trim()}>
        <Plus size={16} /> Add
      </Button>
    </form>
  );
}

function Work({ items, now, onAct, onAdd }: { items: DashboardItem[]; now: number; onAct: (t: SmActionType, it: DashboardItem) => void; onAdd: (title: string, due: string | null) => Promise<unknown> }) {
  const [view, setView] = useState<'open' | 'done'>('open');
  const [module, setModule] = useState<string | null>(null);
  const modules = useMemo(() => [...new Set(items.map((i) => i.module).filter((m): m is string => !!m))].sort(), [items]);
  const shown = items.filter((i) => (view === 'open' ? i.status === 'open' : i.status !== 'open') && (!module || i.module === module)).sort((a, b) => dueKey(a) - dueKey(b));
  const late = shown.filter((i) => i.overdue || dueKey(i) < now);
  const week = shown.filter((i) => !late.includes(i) && dueKey(i) <= now + 7 * DAY);
  const later = shown.filter((i) => !late.includes(i) && dueKey(i) > now + 7 * DAY && dueKey(i) !== Infinity);
  const undated = shown.filter((i) => !late.includes(i) && dueKey(i) === Infinity);
  return (
    <>
      <QuickTodo onAdd={onAdd} />
      <div className="ev-filters">
        <Segmented
          value={view}
          onChange={setView}
          options={[
            { value: 'open', label: 'To do' },
            { value: 'done', label: 'Done (7 days)' },
          ]}
        />
        {modules.length > 1 && (
          <div className="chips">
            <button type="button" className={`chip ${module ? '' : 'on'}`} onClick={() => setModule(null)}>
              All
            </button>
            {modules.map((m) => (
              <button type="button" key={m} className={`chip ${module === m ? 'on' : ''}`} onClick={() => setModule(module === m ? null : m)}>
                {m}
              </button>
            ))}
          </div>
        )}
      </div>
      {!shown.length && <p className="subtle small">{view === 'open' ? 'Nothing to do here.' : 'Nothing finished in the last 7 days.'}</p>}
      {view === 'open' ? (
        <>
          <Group title="Overdue" items={late} now={now} open onAct={onAct} />
          <Group title="Next 7 days" items={week} now={now} open onAct={onAct} />
          <Group title="Later" items={later} now={now} open={false} onAct={onAct} />
          <Group title="No date" items={undated} now={now} open={false} onAct={onAct} />
        </>
      ) : (
        <Group title="Done" items={shown} now={now} open onAct={onAct} />
      )}
    </>
  );
}

function Quotas({ quotas }: { quotas: DashboardQuota[] }) {
  return (
    <div className="ev-quotas">
      {quotas.map((q) => (
        <div key={q.module} className="ev-quota">
          <div className="ev-quota-top">
            <b>{q.module}</b>
            <span className="small subtle">
              {q.done}/{q.total} · {q.pct}%{q.requiredPct != null ? ` · needs ${q.requiredPct}%` : ''}
            </span>
          </div>
          <div className="ev-bar">
            <i className={q.atRisk ? 'risk' : ''} style={{ width: `${Math.min(100, Math.max(0, q.pct))}%` }} />
            {q.requiredPct != null && <b style={{ left: `${Math.min(100, q.requiredPct)}%` }} title={`Needs ${q.requiredPct}%`} />}
          </div>
          {q.atRisk && <div className="tiny ev-risk">At risk</div>}
        </div>
      ))}
    </div>
  );
}

/** Everything that's going on: what's been checked, what needs you, mail, all your work, quizzes, and what School Manager did. */
export function EverythingPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['everything'], queryFn: () => api.get<Everything>('/everything'), refetchInterval: 60_000 });
  const now = useNow(60_000);
  const d = q.data;

  const act = async (type: SmActionType, it: DashboardItem) => {
    try {
      await api.post('/everything/actions', { type, itemId: it.id });
      await qc.invalidateQueries({ queryKey: ['everything'] });
      toast(type === 'done' ? `Ticked off “${it.title.slice(0, 40)}” – School Manager will update its tracker` : 'Reopened');
    } catch (err) {
      toast((err as Error).message, 'error');
    }
  };
  const add = async (title: string, due: string | null) => {
    try {
      await api.post('/everything/actions', { type: 'add', title, due });
      await qc.invalidateQueries({ queryKey: ['everything'] });
      toast('Added – School Manager will track it');
    } catch (err) {
      toast((err as Error).message, 'error');
      throw err;
    }
  };

  if (q.isLoading) return <Loading />;
  if (q.error) {
    const msg = (q.error as Error).message;
    return (
      <div className="page">
        <div className="callout danger">{msg === 'Not found' ? 'This Tempo server needs a restart for the Everything page (close it and run start-tempo.cmd again).' : msg}</div>
      </div>
    );
  }
  if (!d) return null;

  return (
    <div className="page everything">
      <div className="page-head">
        <h1 className="grow">Everything</h1>
        <span className="small faint">{d.generatedAt ? `School Manager updated ${ago(d.generatedAt, now)}` : ''}</span>
        <Button size="sm" variant="ghost" onClick={() => void q.refetch()} loading={q.isFetching} aria-label="Refresh">
          <RotateCcw size={14} />
        </Button>
      </div>
      <RedAlertPanel />
      {!d.available ? (
        <div className="callout">
          Nothing from School Manager yet. Tempo reads <code>C:\School Manager\exports\dashboard.json</code> on your PC (the relay uploads it when Tempo is online).
        </div>
      ) : (
        <>
          <Section id="ev-checked" title="Checked">
            <Checked sources={d.sources} now={now} />
          </Section>
          <Section id="ev-attention" title="Needs you">
            <Attention d={d} now={now} />
          </Section>
          <Section id="ev-mail" title="Email">
            <EmailList e={d.mail} />
          </Section>
          <Section id="ev-work" title="Work">
            <Work items={d.items} now={now} onAct={act} onAdd={add} />
          </Section>
          {d.quotas.length > 0 && (
            <Section id="ev-quizzes" title="Quizzes">
              <Quotas quotas={d.quotas} />
            </Section>
          )}
          <Section id="ev-activity" title="What School Manager did">
            {d.activity.length ? (
              <ul className="ev-activity">
                {d.activity.slice(0, 15).map((a, i) => (
                  <li key={i}>
                    <span className={`ev-dot ${a.status}`} />
                    <div>
                      <div className="small">
                        <b>{a.label}</b> <span className="faint tiny">{when(a.at)}</span>
                      </div>
                      {a.message && <div className="small subtle">{a.message}</div>}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="subtle small">Nothing yet.</p>
            )}
            {d.routines.length > 0 && (
              <details className="ev-group">
                <summary>
                  School Manager's routines <span className="faint">({d.routines.length})</span>
                </summary>
                <ul className="ev-routines">
                  {d.routines.map((r) => {
                    const st = stateOf(r.state);
                    return (
                      <li key={r.key}>
                        <div className="ev-check-top">
                          <b className="small">{r.label}</b>
                          <span className={`badge ${st.tone}`}>{st.label}</span>
                        </div>
                        <div className="tiny faint">
                          {[r.lastOk && `last ran ${ago(r.lastOk, now)}`, r.shouldHaveRun && `scheduled ${when(r.shouldHaveRun)}`].filter(Boolean).join(' · ')}
                        </div>
                        {r.lastMessage && <div className="tiny subtle">{r.lastMessage}</div>}
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
          </Section>
        </>
      )}
    </div>
  );
}
